import { z } from 'zod'
import { fontFamilySchema } from './fonts'
import { hexColorSchema, richTextContentSchema } from './template-primitives'

/**
 * What appears under each headline. 'first-paragraph' was retired (2026-08-25 tech sync): it was
 * broken, only one or two clients used it, and fixing it before launch wasn't worth the effort.
 * 'ai-summary' replaces the report config's inert snippets tab, which wrote settings no renderer
 * ever read — snippets are presentation, so they live here.
 */
export const snippetModeSchema = z.enum(['none', 'matched', 'ai-summary'])

/**
 * Stored documents predate the retirement, so a saved 'first-paragraph' has to keep parsing —
 * a template that throws on read is a report that can't be opened, let alone fixed. It reads as
 * the matched-term snippet, the closest surviving behaviour.
 */
export const storedSnippetModeSchema = z.preprocess(
  (value) => (value === 'first-paragraph' ? 'matched' : value),
  snippetModeSchema,
)
/**
 * How a header's articles are ordered. `priority` follows the header's own outlet order — a saved
 * priority list or the ad-hoc list on the header — with outlets not on it after those that are.
 */
/**
 * `ranked` is the producer's order — outlet authority, then relevance, then date — which is what
 * legacy's DEFAULT sort meant on 9,293 of its 9,327 headers, and the order the data API answers in.
 */
export const articleSortSchema = z.enum(['ranked', 'newest', 'outlet', 'alpha', 'priority'])
export type ArticleSort = z.infer<typeof articleSortSchema>

/** What any article sort needs to know about an article — the renderer's, the editor's, the pull's. */
export interface SortableArticle {
  headline?: string | null | undefined
  outlet?: string | null | undefined
  publishedAt?: string | Date | null | undefined
}

/**
 * The one comparator every article sort uses, so the pull seeding a header, the editor's Sort menu
 * and the renderer agree on what "outlet priority" means. Ties keep the incoming order (every
 * caller sorts with a stable sort), so a priority sort over a relevancy-ordered pull leaves the
 * most relevant story first within each outlet.
 */
export function compareArticles(
  sort: ArticleSort,
  outletPriority: readonly string[] = [],
): (a: SortableArticle, b: SortableArticle) => number {
  const text = (value: string | null | undefined) => (value ?? '').trim().toLocaleLowerCase()
  switch (sort) {
    case 'ranked':
      // The incoming order is the order; every caller sorts stably.
      return () => 0
    case 'newest': {
      const time = (value: SortableArticle['publishedAt']) => {
        if (!value) return Number.NEGATIVE_INFINITY
        const ms = new Date(value).getTime()
        return Number.isNaN(ms) ? Number.NEGATIVE_INFINITY : ms
      }
      return (a, b) => time(b.publishedAt) - time(a.publishedAt)
    }
    case 'outlet':
      return (a, b) => {
        // Unknown outlets after named ones, then alphabetical.
        const ao = text(a.outlet)
        const bo = text(b.outlet)
        if (!ao || !bo) return ao ? -1 : bo ? 1 : 0
        return ao.localeCompare(bo)
      }
    case 'alpha':
      return (a, b) => text(a.headline).localeCompare(text(b.headline))
    case 'priority': {
      const rank = new Map<string, number>()
      outletPriority.forEach((outlet, index) => {
        const key = text(outlet)
        if (key && !rank.has(key)) rank.set(key, index)
      })
      const of = (article: SortableArticle) =>
        rank.get(text(article.outlet)) ?? Number.POSITIVE_INFINITY
      return (a, b) => of(a) - of(b)
    }
  }
}

/**
 * How one report section renders its articles — the structured replacement for the legacy
 * subscription's ~90 render booleans, scoped to a section instead of applying globally.
 * `preset` is a UI affordance (a named starting point), not behavior: the fields below are
 * always the truth the renderer reads.
 */
/**
 * How one part of a rendered article looks. Every field is optional and absent means "leave it
 * alone" — the renderer's own default stands. That is what keeps this honest: a style object is
 * a set of deliberate deviations, not a snapshot of everything, so a renderer change to the
 * default still reaches templates that never asked to differ.
 */
/**
 * Typefaces a template may ask for, with the fallback stack each resolves to at render time.
 *
 * An allowlist rather than free text: this is email, where an unavailable face silently becomes
 * whatever the client feels like, so every entry ends in a generic family. Taken from what the
 * admin portal actually uses — Arial in ~302 of ~380 declarations, Calibri and Aptos for the rest.
 */
// Typefaces live in ./fonts — the catalogue and, more importantly, which target each works in.

export const elementStyleSchema = z.object({
  color: hexColorSchema.optional(),
  /** An override of the template's master font for this element. Absent inherits it. */
  fontFamily: fontFamilySchema.optional(),
  sizePx: z.number().int().min(8).max(40).optional(),
  bold: z.boolean().optional(),
  italic: z.boolean().optional(),
  /**
   * The legacy `.header-name { text-decoration: underline }`, which the default template has and
   * `okla-uhg` deliberately drops. Two of the most-used templates differ by exactly this, so
   * neither could be reproduced without it.
   */
  underline: z.boolean().optional(),
  /**
   * Case and tracking, for the one element the renderer sets both on: the header name is small
   * caps with 1.5px of letter-spacing by default, and the legacy default template's is neither.
   * `uppercase: false` and `letterSpacingPx: 0` are how a template asks for plain title case.
   */
  uppercase: z.boolean().optional(),
  letterSpacingPx: z.number().min(0).max(6).optional(),
  /**
   * Left indent, in px. Not decoration: this is how the legacy templates build hierarchy —
   * universal-template indents its date line 40px and its snippets 80px, and without it a rebuilt
   * template reads as a flat list where the original reads as an outline.
   */
  indentPx: z.number().int().min(0).max(120).optional(),
  /** Space above, in px — the other half of the same job. */
  spaceAbovePx: z.number().int().min(0).max(48).optional(),
  /**
   * Horizontal alignment, for the elements that stand on their own line (a header name, a source
   * heading). The legacy Echo Canyon formatter centres its "Twitter Mentions:" heading; without
   * this a ported template could only put it at the left margin.
   */
  align: z.enum(['left', 'center', 'right']).optional(),
})
export type ElementStyle = z.infer<typeof elementStyleSchema>

/**
 * The parts a template may style independently — the vocabulary taken from what the 56 admin
 * portal templates actually vary. Colour and size carry nearly all of it: 48 of 56 set explicit
 * colours, sizes cluster at 11–14px, and font-family is Arial almost everywhere — which is why the
 * typeface is normally the template's master font (`shell.font`) and an element's `fontFamily` is
 * only the exception.
 *
 * Each key maps to exactly one block the renderer emits, so "what does this control" has a
 * visible answer. Per-source-type keys (a Twitter handle styled apart from a headline, which 45
 * of 56 templates do) extend this object rather than reshaping it.
 */
export const sectionStylesSchema = z.object({
  /**
   * Applies to every element, so "all of this header is 11pt" is one control rather than eight.
   *
   * This is the shape the legacy templates are in: universal-template sets a body font-size and
   * then deviates, and the two largest DCI templates set only three font-size rules between them.
   * Without a base, rebuilding either means repeating the same value on every row and remembering
   * to keep them in step.
   */
  base: elementStyleSchema.optional(),
  /** The header's name above its articles. */
  headerName: elementStyleSchema.optional(),
  /** The headline, and its link. */
  headline: elementStyleSchema.optional(),
  /** The outlet · date · author line. */
  meta: elementStyleSchema.optional(),
  /** The one-line summary, when shown. */
  summary: elementStyleSchema.optional(),
  /** Each snippet under an article. */
  snippet: elementStyleSchema.optional(),
  /** The "Syndicated: …" line. */
  syndication: elementStyleSchema.optional(),
  /** The header's subtitle, when the report gives it one. */
  subHeaderName: elementStyleSchema.optional(),
  /** The full-text "Read More" link on an article. */
  readMore: elementStyleSchema.optional(),
  /**
   * The heading over each source's group inside a header ("News", "Social"), when the header
   * shows them — see `sourceHeadingsSchema`. Per source too, through `stylesBySource`, so the X
   * heading can wear X's black while the news one stays in the brand colour.
   */
  sourceHeading: elementStyleSchema.optional(),
})
export type SectionStyles = z.infer<typeof sectionStylesSchema>

/** Ordered for display, with the labels the editor shows. */
export const STYLED_ELEMENTS: { key: keyof SectionStyles; label: string }[] = [
  { key: 'headerName', label: 'Header name' },
  { key: 'headline', label: 'Headline' },
  { key: 'meta', label: 'Outlet · date · author' },
  { key: 'summary', label: 'Summary line' },
  { key: 'snippet', label: 'Snippets' },
  { key: 'syndication', label: 'Syndicated line' },
  { key: 'subHeaderName', label: 'Header subtitle' },
  { key: 'readMore', label: '“Read more” link' },
  { key: 'sourceHeading', label: 'Source heading' },
]

/**
 * The data sources an article can come from — the same vocabulary the report config attaches to a
 * header, so "style X posts differently" means the same thing on both screens.
 */
export const styleSourceSchema = z.enum([
  'news',
  'x',
  'linkedin',
  'broadcast',
  'podcast',
  'newsletter',
])
export type StyleSource = z.infer<typeof styleSourceSchema>

/**
 * The sources whose stories run in more than one outlet. Syndication is a wire-and-reprint fact
 * about news and broadcast; a post, an episode or a newsletter is published once, by its author,
 * so a "Syndicated" line under one is never true and never shows — not in the email, not on the
 * editor's card, not as a placeholder chip.
 */
export const SYNDICATED_SOURCES: readonly StyleSource[] = ['news', 'broadcast']
export function isSyndicatedSource(source: string | null | undefined): boolean {
  return SYNDICATED_SOURCES.includes((source ?? 'news') as StyleSource)
}

/**
 * The sources whose "headline" is really a post: several sentences written by a person, not a
 * title written by a sub-editor. They are edited in a box rather than on a line, and the account
 * that wrote them — not the words — is what carries the link to the original.
 */
export const AUTHORED_POST_SOURCES: readonly StyleSource[] = ['x', 'linkedin']
export function isAuthoredPost(source: string | null | undefined): boolean {
  return AUTHORED_POST_SOURCES.includes((source ?? 'news') as StyleSource)
}

/**
 * Per-source deviations, layered over the header's base styles.
 *
 * This is the shape the admin portal templates are actually in: 45 of 56 style a Twitter handle
 * apart from a headline, 52 do something specific for podcasts, 30 for broadcast station lines.
 * Expressing that as "the header looks like this, except X posts look like that" is both how the
 * old templates read and far less to hold in your head than six independent style sets.
 *
 * Resolution is per property: a source that sets only a colour inherits the base size, which is
 * what makes the common case — one deviation — a single control rather than a full restatement.
 */
export const stylesBySourceSchema = z.object({
  news: sectionStylesSchema.optional(),
  x: sectionStylesSchema.optional(),
  linkedin: sectionStylesSchema.optional(),
  broadcast: sectionStylesSchema.optional(),
  podcast: sectionStylesSchema.optional(),
  newsletter: sectionStylesSchema.optional(),
})
export type StylesBySource = z.infer<typeof stylesBySourceSchema>

export const STYLE_SOURCES: { key: StyleSource; label: string }[] = [
  { key: 'news', label: 'News' },
  { key: 'x', label: 'X' },
  { key: 'linkedin', label: 'LinkedIn' },
  { key: 'broadcast', label: 'Broadcast' },
  { key: 'podcast', label: 'Podcasts' },
  { key: 'newsletter', label: 'Newsletters' },
]

/**
 * Tokens that mean something inside ONE article's markup, as opposed to {@link MERGE_FIELDS},
 * which are facts about the report. Same `{{token}}` syntax so there is one thing to learn.
 *
 * Everything is escaped on the way in except `snippets`, which is producer HTML carrying the
 * matched-term `<mark>` highlights and would be useless escaped.
 */
export const ARTICLE_FIELDS = [
  { token: 'headline', label: 'Headline', sample: 'Acme names a new chief executive' },
  { token: 'url', label: 'Article link', sample: 'https://example.com/a' },
  { token: 'outlet', label: 'Outlet', sample: 'The Example Times' },
  { token: 'date', label: 'Date', sample: 'Jun 24, 2026' },
  { token: 'author', label: 'Author', sample: 'A. Reporter' },
  { token: 'summary', label: 'Summary', sample: 'A one-line summary of the piece.' },
  { token: 'snippets', label: 'Snippets', sample: '…the matched text…' },
  { token: 'full_text_url', label: 'Full-text link', sample: 'https://licensed.example.com/a' },
  { token: 'syndicated', label: 'Syndicated outlets', sample: 'Yahoo News, AOL' },
  /**
   * The legacy default's shapes: a long date with the time, one `<li>` per snippet, and the
   * syndicated outlets as links to their copies — what a row ported from `default/template.html`
   * needs that the short forms above cannot give it.
   */
  { token: 'date_long', label: 'Date, long', sample: 'Wednesday, June 24, 08:00:00 AM' },
  {
    token: 'snippet_items',
    label: 'Snippets as list items',
    sample: '<li>…the matched text…</li>',
  },
  {
    token: 'syndicated_links',
    label: 'Syndicated outlets, linked',
    sample:
      '<a href="https://example.com/copy-1" target="_blank"><i>Yahoo News</i></a>,  <a href="https://example.com/copy-2" target="_blank"><i>AOL</i></a>',
  },
  // Producer-stamped, and the reason `emerald`'s chips exist. Absent on most articles, which is
  // why a token with no value has to render as nothing rather than as an empty chip.
  { token: 'sentiment', label: 'Sentiment', sample: 'Positive' },
  { token: 'reach', label: 'Reach', sample: '12.4K' },
  { token: 'engagement', label: 'Engagement', sample: '843' },
  { token: 'handle', label: 'Social handle', sample: '@example' },
  // The author's audience at collection, formatted ("1.6M"); X posts only, and only when the
  // collector kept it — the legacy Echo Canyon formatter's "[X; 1.6M followers]".
  { token: 'followers', label: 'Followers', sample: '1.6M' },
  // What the header calls this article's source — the label from `sourceHeadings`, so the badge
  // on a social card and the heading over its group can never disagree about the name.
  { token: 'source_label', label: 'Source label', sample: 'News' },
] as const
export type ArticleFieldToken = (typeof ARTICLE_FIELDS)[number]['token']

export function articleFieldLabel(token: string): string {
  return ARTICLE_FIELDS.find((field) => field.token === token)?.label ?? token
}

/**
 * Hand-written markup for one article row, replacing the built-in layout entirely.
 *
 * The escape hatch for the two templates in the admin portal that are designed emails rather than
 * styled clip lists — a 600px table card with a sentiment chip and a Read More button is not
 * reachable by any number of colour and size controls, and adding primitives until it were would
 * mean carrying layout machinery for 2 of 56.
 *
 * Per source, because that is the axis these differ on, with `base` covering the rest. A source
 * with no entry keeps the built-in layout, so this is opt-in one source at a time rather than an
 * all-or-nothing switch out of the formatting controls.
 */
export const articleHtmlSchema = z.object({
  base: richTextContentSchema.optional(),
  news: richTextContentSchema.optional(),
  x: richTextContentSchema.optional(),
  linkedin: richTextContentSchema.optional(),
  broadcast: richTextContentSchema.optional(),
  podcast: richTextContentSchema.optional(),
  newsletter: richTextContentSchema.optional(),
})
export type ArticleHtml = z.infer<typeof articleHtmlSchema>

/**
 * The pieces of an article row, as things that can be arranged.
 *
 * Today outlet, date and author are fused into one "meta" line by the renderer, which is why
 * neither reviewer could move them: they are not separate anywhere. Naming them individually is
 * what makes "put the outlet on its own line above the headline" expressible at all.
 */
export const ARTICLE_PARTS = [
  { key: 'headline', label: 'Headline' },
  { key: 'outlet', label: 'Outlet' },
  { key: 'date', label: 'Date' },
  { key: 'author', label: 'Author' },
  { key: 'summary', label: 'One-line summary' },
  { key: 'snippets', label: 'Snippets' },
  { key: 'syndication', label: 'Syndication' },
  { key: 'readMore', label: '“Read more” link' },
] as const
export type ArticlePart = (typeof ARTICLE_PARTS)[number]['key']
const ARTICLE_PART_KEYS = ARTICLE_PARTS.map((part) => part.key) as [ArticlePart, ...ArticlePart[]]
export const articlePartSchema = z.enum(ARTICLE_PART_KEYS)

/** One line of an article row: the parts on it, in order, and the gap above it. */
export const articleLineSchema = z.object({
  parts: z.array(articlePartSchema).min(1).max(8),
  spaceAbovePx: z.number().int().min(0).max(48).optional(),
})
export type ArticleLine = z.infer<typeof articleLineSchema>

/**
 * How an article's parts are arranged, when a template says.
 *
 * EMPTY MEANS THE BUILT-IN ARRANGEMENT, and empty is the default — so every template written
 * before this renders byte-for-byte as it did. Arranging is opt-in, and the renderer keeps two
 * paths rather than trying to express its own historical layout in this model and hoping the
 * output matches.
 *
 * Visibility stays where it was: the field switches, the snippet mode and the syndication toggle
 * still decide WHETHER a part appears. This decides only where it sits. Keeping the two separate
 * is what stops a part vanishing because somebody rearranged a line — the mistake that would make
 * this feature worse than not having it.
 */
export const articleLinesSchema = z.array(articleLineSchema).max(12)

/**
 * The built-in arrangement, written out — what someone starts from when they begin arranging.
 *
 * Mirrors the order the renderer's own layout produces, so the first thing you see after clicking
 * "arrange" is what you already had. Beginning from an empty canvas would make the feature feel
 * like a rebuild rather than an adjustment, which is the opposite of what was asked for.
 */
export function defaultArticleLines(): ArticleLine[] {
  return [
    { parts: ['headline'] },
    { parts: ['outlet', 'date', 'author'] },
    { parts: ['readMore'] },
    { parts: ['summary'] },
    { parts: ['snippets'] },
    { parts: ['syndication'] },
  ]
}

/**
 * What one data source may say differently from the rest of its header.
 *
 * Styles and hand-written markup could already vary by source; the things that decide WHAT an
 * article shows could not. That asymmetry is what forced people into custom HTML to make an X
 * post read differently from an article — the appearance was reachable and the content was not.
 *
 * Partial on purpose: an override says only what differs, so a source that just wants two
 * snippets instead of three does not restate the whole presentation and quietly freeze the rest
 * at today's values.
 */
export const presentationOverrideSchema = z.object({
  fields: z
    .object({
      outlet: z.boolean(),
      date: z.boolean(),
      author: z.boolean(),
      summary: z.boolean(),
      readMore: z.boolean(),
    })
    .partial()
    .optional(),
  snippets: z
    .object({
      mode: storedSnippetModeSchema,
      limit: z.number().int().min(1).max(10),
      highlightTerms: z.boolean(),
      style: z.enum(['block', 'bullets']),
      highlightColor: hexColorSchema,
      principalHighlightColor: hexColorSchema,
    })
    .partial()
    .optional(),
  syndication: z
    .object({
      show: z.boolean(),
      limit: z.number().int().min(1).max(50),
      sort: z.enum(['newest', 'alpha']),
    })
    .partial()
    .optional(),
  /** A whole arrangement, not a partial one — half an arrangement means nothing. */
  lines: articleLinesSchema.optional(),
  outletPlacement: z.enum(['meta', 'beforeHeadline']).optional(),
})
export type PresentationOverride = z.infer<typeof presentationOverrideSchema>

export const presentationBySourceSchema = z.object({
  news: presentationOverrideSchema.optional(),
  x: presentationOverrideSchema.optional(),
  linkedin: presentationOverrideSchema.optional(),
  broadcast: presentationOverrideSchema.optional(),
  podcast: presentationOverrideSchema.optional(),
  newsletter: presentationOverrideSchema.optional(),
})
export type PresentationBySource = z.infer<typeof presentationBySourceSchema>

const sourceLabelSchema = z.string().min(1).max(60)

/**
 * A heading over each source's group inside a header: the news rows, then "Social", then the
 * podcasts. The producer already delivers a header's articles grouped by source; this is what
 * lets a reader see the seams.
 *
 * Both halves are the client's to change, because both are what clients ask about. The CONTENT is
 * `labels` — a header may call X posts "X", "Twitter" or "Social", and The Trade Desk's badge
 * says "in" for LinkedIn — and the same label feeds the `{{source_label}}` token in custom
 * article markup, so a badge and a heading never disagree. The APPEARANCE is the `sourceHeading`
 * element in the header's styles, per source when wanted, exactly like every other element.
 *
 * Off by default so nothing already built changes. `onlyWhenMixed` is on by default because a
 * header that is all news gains nothing from a "News" label over it. `sources` narrows the
 * headings to some of a header's sources: the legacy Echo Canyon template heads its X posts
 * "Twitter Mentions:" and leaves the news above them unlabelled, and a template ported from it
 * has to be able to say so. Absent, every source present gets its heading.
 */
export const sourceHeadingsSchema = z.object({
  show: z.boolean().default(false),
  onlyWhenMixed: z.boolean().default(true),
  /** Only these sources get a heading; absent means all of them. */
  sources: z.array(styleSourceSchema).max(STYLE_SOURCES.length).optional(),
  /** What each source is called here. Absent means the product's own name for it. */
  labels: z
    .object({
      news: sourceLabelSchema,
      x: sourceLabelSchema,
      linkedin: sourceLabelSchema,
      broadcast: sourceLabelSchema,
      podcast: sourceLabelSchema,
      newsletter: sourceLabelSchema,
    })
    .partial()
    .default({}),
  /** A hairline beneath each heading, in the accent. */
  rule: z.boolean().default(false),
})
export type SourceHeadings = z.infer<typeof sourceHeadingsSchema>

export function defaultSourceHeadings(): SourceHeadings {
  return { show: false, onlyWhenMixed: true, labels: {}, rule: false }
}
